import React, { useEffect, useState } from 'react';
import { Plus, X } from 'lucide-react';
import { apiGet } from '../../lib/api';
import { inr2 } from '../../lib/format';

export const CHARGE_TYPES = ['Transport / Freight', 'Loading / Unloading', 'Packing & Forwarding', 'Insurance', 'Other'];
const RATES = [0, 5, 12, 18, 28];
const uid = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `c${Date.now()}${Math.random()}`);

/** Default GST % for a new charge row = highest GST rate among the PO lines. */
export const maxLineRate = (items) => Math.max(0, ...(items || []).map(i => Number(i.gst_rate) || 0));

/** Old PO → editable rows from its three fixed amounts. */
export const chargesFromPo = (po) => {
  if (Array.isArray(po?.charges)) return po.charges.map(c => ({ ...c }));
  const rows = [];
  if (Number(po?.freight_amount)) rows.push({ id: 'legacy-freight', type: 'Transport / Freight', description: '', amount: po.freight_amount, gst_rate: po.freight_gst_rate ?? 18 });
  if (Number(po?.loading_unloading_amount)) rows.push({ id: 'legacy-loading', type: 'Loading / Unloading', description: '', amount: po.loading_unloading_amount, gst_rate: 0 });
  if (Number(po?.other_charges)) rows.push({ id: 'legacy-other', type: 'Other', description: '', amount: po.other_charges, gst_rate: 0 });
  return rows;
};

export const cleanCharges = (rows) => (rows || []).filter(c => Number(c.amount) > 0)
  .map(c => ({ id: c.id, type: c.type, description: c.description || '', amount: Number(c.amount), gst_rate: Number(c.gst_rate), project_id: c.project_id || undefined }));

const RateCell = ({ value, onChange, testid }) => {
  const custom = !RATES.includes(Number(value));
  return (
    <div className="flex gap-1">
      <select value={custom ? 'custom' : String(Number(value))} data-testid={testid}
        onChange={e => onChange(e.target.value === 'custom' ? (custom ? value : 1) : Number(e.target.value))}
        className="px-1.5 py-1 border border-slate-300 rounded-sm text-xs bg-white">
        {RATES.map(r => <option key={r} value={r}>{r} %</option>)}
        <option value="custom">Custom</option>
      </select>
      {custom && <input type="number" min="0" max="28" step="0.01" value={value} data-testid={`${testid}-custom`}
        onChange={e => onChange(Math.min(28, Math.max(0, Number(e.target.value))))}
        className="w-16 px-1.5 py-1 border border-slate-300 rounded-sm text-xs" />}
    </div>
  );
};

/** "Other charges" table — every row has its own GST rate (#4). */
export function ChargesTable({ charges, onChange, items, projects = [], intra = true, testid = 'charges' }) {
  const multi = projects.length > 1;
  const set = (i, patch) => onChange(charges.map((c, j) => (j === i ? { ...c, ...patch } : c)));
  const add = () => onChange([...charges, { id: uid(), type: 'Transport / Freight', description: '', amount: '', gst_rate: maxLineRate(items), project_id: projects[0]?.id }]);
  const cell = 'px-2 py-1.5 border-b border-slate-100';
  return (
    <div className="border border-slate-200 rounded-sm" data-testid={`${testid}-table`}>
      <div className="flex items-center justify-between px-3 py-2 bg-slate-50 border-b border-slate-200">
        <span className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500">Other charges</span>
        <button type="button" onClick={add} className="flex items-center gap-1 text-xs text-[#3b4872] font-semibold" data-testid={`${testid}-add-btn`}>
          <Plus size={12} /> Add charge
        </button>
      </div>
      {charges.length === 0 ? <div className="px-3 py-2 text-xs text-slate-400">No charges</div> : (
        <table className="w-full text-xs">
          <thead><tr className="text-slate-500 text-left">
            <th className={cell}>Type</th><th className={cell}>Description</th><th className={`${cell} text-right`}>Amount</th>
            <th className={cell}>GST %</th>{multi && <th className={cell}>Project</th>}
            <th className={`${cell} text-right`}>GST</th><th className={`${cell} text-right`}>Total</th><th className={cell} />
          </tr></thead>
          <tbody>
            {charges.map((c, i) => {
              const gst = (Number(c.amount) || 0) * (Number(c.gst_rate) || 0) / 100;
              return (
                <tr key={c.id || i} data-testid={`${testid}-row-${i}`}>
                  <td className={cell}><select value={c.type} onChange={e => set(i, { type: e.target.value })} className="px-1.5 py-1 border border-slate-300 rounded-sm text-xs bg-white" data-testid={`${testid}-type-${i}`}>
                    {CHARGE_TYPES.map(t => <option key={t}>{t}</option>)}</select></td>
                  <td className={cell}><input value={c.description || ''} onChange={e => set(i, { description: e.target.value })} placeholder="e.g. Nashik to site" className="w-full px-1.5 py-1 border border-slate-300 rounded-sm text-xs" data-testid={`${testid}-desc-${i}`} /></td>
                  <td className={cell}><input type="number" min="0" step="0.01" value={c.amount} onChange={e => set(i, { amount: e.target.value })} className="w-24 px-1.5 py-1 border border-slate-300 rounded-sm text-xs text-right" data-testid={`${testid}-amount-${i}`} /></td>
                  <td className={cell}><RateCell value={c.gst_rate} onChange={v => set(i, { gst_rate: v })} testid={`${testid}-rate-${i}`} /></td>
                  {multi && <td className={cell}><select value={c.project_id || ''} onChange={e => set(i, { project_id: e.target.value })} className="px-1.5 py-1 border border-slate-300 rounded-sm text-xs bg-white" data-testid={`${testid}-project-${i}`}>
                    {projects.map(p => <option key={p.id} value={p.id}>{p.code || p.name}</option>)}</select></td>}
                  <td className={`${cell} text-right tabular-nums`} data-testid={`${testid}-gst-${i}`} title={intra ? 'CGST+SGST' : 'IGST'}>{inr2(gst)}</td>
                  <td className={`${cell} text-right tabular-nums font-semibold`} data-testid={`${testid}-total-${i}`}>{inr2((Number(c.amount) || 0) + gst)}</td>
                  <td className={cell}><button type="button" onClick={() => onChange(charges.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-600" data-testid={`${testid}-remove-${i}`}><X size={13} /></button></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

/** #13 — "Place of supply: X · Vendor state: Y → CGST+SGST / IGST" before saving. */
export function GstSplitHint({ vendorId, projectId, deliveryState, onSplit, testid = 'gst-split-hint' }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    if (!vendorId) { setD(null); return; }
    apiGet('/po-gst-preview', { vendor_id: vendorId || undefined, project_id: projectId || undefined, delivery_state: deliveryState || undefined })
      .then(r => { setD(r); onSplit?.(r.gst_split_type); }).catch(() => setD(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorId, projectId, deliveryState]);
  if (!d) return null;
  return (
    <div className="text-[11px] text-slate-600 bg-slate-50 border border-slate-200 rounded-sm px-2 py-1" data-testid={testid}>
      Place of supply: <b>{d.place_of_supply || '—'}</b> · Vendor state: <b>{d.vendor_state || '—'}</b> → <b className="text-[#3b4872]">{d.label}</b>
      {d.vendor_state_missing && <span className="text-amber-700"> (vendor state unknown — defaults to CGST+SGST)</span>}
    </div>
  );
}
